import React, { useState, useEffect, useRef } from 'react';
import styles from './TestimonialCarousel.module.css';
import googleIcon from "../../src/assets/icons/google.svg"
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

const TestimonialCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoSliding, setIsAutoSliding] = useState(true);
    const autoSlideInterval = useRef(null);
    const { t } = useTranslation();
    
    const testimonials = [
        {
            rating: 5,
            text: t('Amazing service by Matt and the team! Their communication was excellent and they explained everything thoughtfully, which was especially important being a first-time-loaner. The comparison meeting and how-to video for signing the documentation was especially helpful! Would highly recommend.'),
            author: "Tamara Spakman",
            date: "2024-10-12",
            icon: "T",
            color: "#1e1e1e"
        },
        {
            rating: 5,
            text: t(`We would like to thank Matthew and his team for recently helping us to renegotiate the interest rates for our existing Investment property loans. We have had previous dealings with him over the past years and have always found him very knowledgeable in his profession and easy to deal with. He and Isah found us the best deal by providing us with interest rates from several banks to choose from. The set up of the loans were done via email and digitally which made life easy for all of us without having to meet and sign paperwork etc.`),
            author: "Christopher Potter",
            date: "2024-09-29",
            icon: "C",
            color: "#956c63" 
        },
        {
            rating: 5,
            text: t(`Incredible Support for First-Time Buyers! I can't thank Key Choice Lending enough for helping me through the process of buying my first home. From start to finish, Matt and the team were there to guide me, making sure I understood every step. They were always available for any questions I had and made what could have been a stressful experience feel smooth and exciting. I'm so grateful for their professionalism and support—I highly recommend Key Choice Lending!`),
            author: "Maddi Parsons",
            date: "2024-08-17",
            icon: "M",
            color: "#000000"
        },
        {
            rating: 5,
            text: t(`A stress free first home buying experience! My sister and I went through key choice, as first home buyers, we couldn't be happier! we went into this process unsure and not confident but Matt, Ron and the team have made this long journey nothing but a breeze!! they've guided us through this process with so much patience, answering any questions we had straight away and made sure we felt comfortable from start to finish. I couldn't recommend this team enough!`),
            author: "Darcie Gray",
            date: "2024-08-15",
            icon: "D",
            color: "#9a4a4a"
        },
        {
            rating: 5,
            text: t("Key Choice Lending exceeded our expectations in every way. When we decided to change the structure of our purchase mid-process, they quickly adjusted their approach to ensure we could still buy our dream home. Their responsiveness and deep understanding of the market made a huge difference in what could have been a stressful situation. The team's dedication and attention to detail left us feeling completely supported and confident in our decisions. We couldn't be happier with the outcome and highly recommend their services!"),
            author: "Kristy Loveday",
            date: "2024-09-12",
            icon: "K",
            color: "#000000"
        },
        {
            rating: 5,
            text: t("We can't thank Matt and his team enough, they were well organised and really knew what they were doing."),
            author: "Sarah Wallace",
            date: "2024-09-09",
            icon: "S",
            color: "#378637"
        },
        {
            rating: 5,
            text: t(`Definitely recommend using Key Choice Lending for mortgage or lending needs. Their service was prompt and professional every step of the way. Such a friendly and approachable team. Nothing was a bother during the process, and their communication and professionalism was above and beyond what we had expected. Thanks again Matthew and the team at Key Choice. 10/10 for customer service and satisfaction.`),
            author: "leah lorenzutta",
            date: "2024-08-22",
            icon: "L",
            color: "#078a07"
        },
        {
            rating: 5,
            text: t("Thank you to the Key Choice Lending team for assisting us in purchasing our new home while we hadn't yet sold our current one. We can now prepare our home to maximize the sale price and have time to move while continuing to work and manage a family. You made our complex circumstances easy to navigate."),
            author: "Sarah Palmer",
            date: "2024-08-20",
            icon: "S",
            color: "#3e4a4a"
        },
        {
            rating: 5,
            text: t(`You made the transactions very simple and swift to refresh our mortgage arrangements. Thanks team!`),
            author: "GECC Circle of six",
            date: "2024-08-16",
            icon: "G",
            color: "#4a4a55"
        }
    ];

    const handlePrev = () => {
        setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
        resetAutoSlide();
    };

    const handleNext = () => {
        setCurrentIndex(prevIndex => Math.min(prevIndex + 1, testimonials.length - 4));
        resetAutoSlide();
    };

    const startAutoSlide = () => {
        if (autoSlideInterval.current) {
            clearInterval(autoSlideInterval.current);
        }
        autoSlideInterval.current = setInterval(() => {
            setCurrentIndex(prevIndex => {
                if (prevIndex >= testimonials.length - 4) {
                    return 0;
                }
                return prevIndex + 1;
            });
        }, 5000); // Change slide every 5 seconds
    };

    const resetAutoSlide = () => {
        setIsAutoSliding(false);
        if (autoSlideInterval.current) {
            clearInterval(autoSlideInterval.current);
        }
        setTimeout(() => {
            setIsAutoSliding(true);
            startAutoSlide();
        }, 10000); // Resume auto-sliding after 10 seconds of inactivity
    };

    useEffect(() => {
        if (isAutoSliding) {
            startAutoSlide();
        }
        return () => {
            if (autoSlideInterval.current) {
                clearInterval(autoSlideInterval.current);
            }
        };
    }, [isAutoSliding]);

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === testimonials.length - 4;

    return (
        <div className={styles.container}>
            <div className={styles.carouselWrapper}>
                {!isFirstSlide && (
                    <button
                        className={`${styles.navButton} ${styles.prevButton}`}
                        onClick={handlePrev}
                        aria-label="Previous testimonial"
                    >
                        &#10094;
                    </button>
                )}
                <div className={styles.testimonialContainer}>
                    {testimonials.map((testimonial, index) => (
                        <div
                            key={index}
                            className={styles.testimonialCard}
                            style={{
                                transform: `translateX(calc(${(index - currentIndex) * 100}% + ${(index - currentIndex) * 15}px))`,
                                opacity: index >= currentIndex && index < currentIndex + 4 ? 1 : 0,
                                visibility: index >= currentIndex && index < currentIndex + 4 ? 'visible' : 'hidden',
                                transition: 'all 0.5s ease'
                            }}
                        >
                            <div
                                className={styles.authorIcon}
                                style={{ backgroundColor: testimonial.color }}
                            >
                                {testimonial.icon}
                            </div>
                            <div className={styles.stars}>
                                {[...Array(testimonial.rating)].map((_, i) => (
                                    <span key={i} className={styles.star}>★</span>
                                ))}
                            </div>
                            <p className={styles.testimonialText}>
                                {testimonial.text}
                            </p>
                            <div className={styles.authorInfo}>
                                <div className={styles.authorDetails}>
                                    <p className={styles.authorName}>{testimonial.author}</p>
                                    <p className={styles.testimonialDate}>{testimonial.date}</p>
                                </div>
                                <div className={styles.googleBadge}>
                                    <Image
                                        src={googleIcon}
                                        width={25}
                                        height={25}
                                        alt="google"
                                        aria-hidden="false"
                                        role="img"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {!isLastSlide && (
                    <button
                        className={`${styles.navButton} ${styles.nextButton}`}
                        onClick={handleNext}
                        aria-label="Next testimonial"
                    >
                        &#10095;
                    </button>
                )}
            </div>
        </div>
    );
};

export default TestimonialCarousel;